import { useCallback } from "react";
import { ModalBase } from "../ModalBase";
import { Wizard } from "components/wizards/Wizard";
import { useDispatch } from "react-redux";
import { SelectDestinationStep } from "./steps/SelectDestination";
import { PlatformSettings } from "./steps/PlatformSettings";
import { LivestreamDetailsStep } from "./steps/LivestreamDetails";
import { ProcessingStep } from "./steps/Processing";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import {
    updateGeneralForm,
    updateVideoPlayerForm
} from "store/platforms/slice";
import {
    GeneralPlatformForm,
    QualitySelection,
    VideoPlayerForm
} from "store/platforms/types";
import { useSpeedTest } from "hooks/useSpeedTest";
import { useNewLivestreamStepLogic } from "./useNewLivestreamStepLogic";
import { NewLivestreamModalSteps } from "./types";

export const NewLivestreamModal = ({ reload }: { reload: () => void }) => {
    const dispatch = useDispatch();
    const {
        isSubmitting,
        setIsSubmitting,
        setSubmissionStatus,
        activeStep,
        setActiveStep,
        setSelectedPlatform,
        selectedDestinationId,
        setSelectedDestinationId,
        selectedPlatform,
        setHasAddedPlatformSpecificSettings,
        setCurrentFacebookEdge,
        validationErrors,
        setOnAdvanceStep,
        advanceStep,
        advanceButtonText,
        setOnDismiss,
        dismissButtonAction,
        dismissButtonText,
        canProceedToNextStep,
        modalHeaderText
    } = useNewLivestreamStepLogic({ reload });

    const onGeneralFormChange = useCallback(
        (update: Partial<GeneralPlatformForm>) => {
            dispatch(updateGeneralForm(update));
        },
        [dispatch]
    );

    const onVideoPlayerFormChange = useCallback(
        (update: Partial<VideoPlayerForm>) => {
            dispatch(updateVideoPlayerForm(update));
        },
        [dispatch]
    );

    const handleSetStreamQuality = useCallback(
        (quality: QualitySelection) =>
            onGeneralFormChange({ selectedQuality: quality }),
        [onGeneralFormChange]
    );

    const { speedTestStatus, results, runSpeedTest } = useSpeedTest({
        onCompleted: (results) => {
            handleSetStreamQuality(results?.suggestedSetting);
        }
    });

    return (
        <ModalBase
            isOpen
            header={modalHeaderText}
            successButton={advanceButtonText}
            onSuccess={advanceStep}
            dismissButton={dismissButtonText}
            onDismiss={dismissButtonAction}
            successDisabled={!canProceedToNextStep}
            shouldNotCloseOnSuccess={true}
        >
            <div>
                <Wizard<NewLivestreamModalSteps>
                    activeStep={activeStep}
                    steps={[
                        {
                            id: NewLivestreamModalSteps.SelectDestination,
                            component: (
                                <SelectDestinationStep
                                    setSelectedPlatform={setSelectedPlatform}
                                    advanceToPlatformSettings={(
                                        destination?: DestinationDescriptor
                                    ) => {
                                        if (destination) {
                                            setSelectedDestinationId(
                                                destination.id
                                            );
                                            setHasAddedPlatformSpecificSettings(
                                                true
                                            );
                                        }
                                        setActiveStep(
                                            NewLivestreamModalSteps.PlatformSettings
                                        );
                                    }}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.PlatformSettings,
                            component: (
                                <PlatformSettings
                                    selectedPlatform={selectedPlatform}
                                    setHasAddedPlatformSpecificSettings={
                                        setHasAddedPlatformSpecificSettings
                                    }
                                    setOnAdvanceStep={setOnAdvanceStep}
                                    selectedDestinationId={
                                        selectedDestinationId
                                    }
                                    advanceStep={advanceStep}
                                    setOnDismiss={setOnDismiss}
                                    setCurrentFacebookEdge={
                                        setCurrentFacebookEdge
                                    }
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.LivestreamDetails,
                            component: (
                                <LivestreamDetailsStep
                                    speedTestStatus={speedTestStatus}
                                    speedTestResults={results}
                                    handleRetrySpeedTest={runSpeedTest}
                                    onGeneralFormChange={onGeneralFormChange}
                                    onVideoPlayerFormChange={
                                        onVideoPlayerFormChange
                                    }
                                    setStreamQuality={handleSetStreamQuality}
                                    validationErrors={validationErrors}
                                    setOnAdvanceStep={setOnAdvanceStep}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.Processing,
                            component: (
                                <ProcessingStep
                                    isSubmitting={isSubmitting}
                                    setIsSubmitting={setIsSubmitting}
                                    setSubmissionStatus={setSubmissionStatus}
                                />
                            )
                        }
                    ]}
                />
            </div>
        </ModalBase>
    );
};
